<script lang="ts" setup>
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'mdi-vue3'
import { computed, type HTMLAttributes, markRaw } from 'vue'
import { useI18n } from 'vue-i18n'

import { type AButtonProperties } from '../AButton/AButton.vue'
import AButtons, { type AButtonsItemInterface } from '../AButton/AButtons.vue'

defineOptions({ inheritAttrs: true })

export type TablePaginationProperties = {
  class?: HTMLAttributes['class']
  size?: AButtonProperties['size']
  page?: number
  pageCount?: number
}

const properties = withDefaults(defineProps<TablePaginationProperties>(), {
  class: '',
  size: 'sm',
  page: 1,
  pageCount: 1,
})

type TablePaginationModel = never

type TablePaginationEmits = {
  (event: 'change', page: number): void
}

type TablePaginationExpose = never

type TablePaginationSlots = never

export type TablePaginationTypes = {
  model: TablePaginationModel
  properties: TablePaginationProperties
  emits: TablePaginationEmits
  expose: TablePaginationExpose
  slots: TablePaginationSlots
}

const emit = defineEmits<TablePaginationEmits>()
const { t } = useI18n({
  messages: {
    en: {
      'Page {page}': 'Page {page}',
      'Page {page} of {pageCount}': 'Page {page} of {pageCount}',
    },
    nl: {
      'Page {page}': 'Pagina {page}',
      'Page {page} of {pageCount}': 'Pagina {page} van {pageCount}',
    },
    de: {
      'Page {page}': 'Seite {page}',
      'Page {page} of {pageCount}': 'Seite {page} von {pageCount}',
    },
  },
})

const classes = computed(() => {
  return ['atabix-data-table-pagination', properties.class]
})

const buttons = computed<AButtonsItemInterface[]>(() => [
  {
    variant: 'outline',
    size: properties.size,
    icon: markRaw(ChevronDoubleLeftIcon),
    click: onClickFirstPage,
    disabled: properties.page <= 1,
  },
  {
    variant: 'outline',
    size: properties.size,
    icon: markRaw(ChevronLeftIcon),
    click: onClickPreviousPage,
    disabled: properties.page <= 1,
  },
  {
    variant: 'outline',
    size: properties.size,
    icon: markRaw(ChevronRightIcon),
    click: onClickNextPage,
    disabled: properties.pageCount !== undefined && properties.page >= properties.pageCount,
  },
  {
    variant: 'outline',
    size: properties.size,
    icon: markRaw(ChevronDoubleRightIcon),
    click: onClickLastPage,
    visible: properties.pageCount !== undefined,
    disabled: properties.pageCount !== undefined && properties.page >= properties.pageCount,
  },
])

function onClickFirstPage() {
  emit('change', 1)
}

function onClickNextPage() {
  const count = properties.page + 1
  emit('change', properties.pageCount ? Math.min(count, properties.pageCount) : count)
}

function onClickPreviousPage() {
  emit('change', Math.max(properties.page - 1, 1))
}

function onClickLastPage() {
  if (properties.pageCount === undefined) return

  emit('change', properties.pageCount)
}

const pageLabel = computed(() => {
  return properties.pageCount === undefined
    ? t('Page {page}', { page: properties.page })
    : t('Page {page} of {pageCount}', { page: properties.page, pageCount: properties.pageCount })
})

//
</script>

<template>
  <div :class="classes">
    {{ pageLabel }}
    <AButtons class="gap-2" :items="buttons" sticky />
  </div>
</template>

<style lang="scss">
.atabix-data-table-pagination {
  @apply flex flex-row items-center text-base justify-between;
  @apply gap-4;
}

.atabix-table-wrapper--mobile {
  .atabix-data-table-pagination {
    @apply w-full;
  }
}
</style>
